import { 
    MESSAGE_HEADER_LEN_BYTES, 
    MessagePayloadType, 
    MessageStruct, 
    parseMessageStruct, 
    getMessageJsonPayload 
  } from './message';
  import { 
    getDataViewOfTypedArray, 
    Maybe, 
    NoArgsVoidFunc, 
    noopFunc, 
    Nullable, 
    perf 
  } from './utils';
  
  import { VideoCodecInfo, AudioCodecInfo, getVideoCodecName, getAudioCodecName } from './av';
  
  export interface TStreamEvent {
    Type: 'Description' | 'Eos' | 'onMetaData';
  }
  
  export interface TStreamDescription extends TStreamEvent {
    GroupId: number;
    Tag: TStreamGroupTag;
  }
  
  export enum TStreamGroupTag {
    Empty = '',
    Audio = 'AUDIO',
    Video0 = 'VIDEO_0',
    Video1 = 'VIDEO_1',
    Video2 = 'VIDEO_2',
    Metadata = 'METADATA',
  }
  
  interface StreamSegment {
    description: Nullable<TStreamDescription>;
    isEnded: boolean;
    buffer: MessageStruct[];
    bufferReadOffset: number;
  }
  
  export class StreamReader {
    private _rxDataBuf: Nullable<Uint8Array> = null;
    private _rxDataOffset = 0;
    private _isClosed = false;
    private _bytesCnt = 0;
    private _lastParseTime = NaN;
    private _accuMsgDeltaMs = NaN;
    private _accuMsgCnt = 0;
    private _currentMeanMsgDeltaMs = NaN;
    public minReadTimeoutMs = 160;
    private _shouldClose = false;
    private _reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    private _writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
    private _consumePromise: Promise<void> | null = null;
    onClose: (() => void) | null = null;
    onRead: (msg: MessageStruct, groupId: number, tag: string, segmentIndex: number) => void = noopFunc;
    onDescription: (segmentIndex: number, descr: TStreamDescription) => void = noopFunc;
    onEos: (segmentIndex: number) => void = noopFunc;
    onMessage: (msg: MessageStruct, groupId: number, tag: string) => void = noopFunc;
    onVideoCodecInfo: (codecInfo: VideoCodecInfo) => void = noopFunc;
    onAudioCodecInfo: (codecInfo: AudioCodecInfo) => void = noopFunc;
  
    private _timeoutId: any = null;
    private _groupId: number = -1;
    private _tag: string = '';
    private _segmentList: StreamSegment[] = [];
    private _segmentMap: Map<number, [number, StreamSegment]> = new Map();
  
    constructor(
      private _streamIndex: number,
      private _incomingStream: Nullable<WebTransportBidirectionalStream>,
      public readTimeoutMs: number = 2000
    ) {}
  
    async consume() {
      console.log('StreamReader.consume', this._streamIndex);
  
      if (!this._incomingStream) {
        console.log(`StreamReader ${this._streamIndex}: No incoming stream to consume.`);
        return;
      }
  
      if (this._reader) {
        console.error(`StreamReader ${this._streamIndex}: Reader already exists.`);
        return;
      }
  
      this._reader = this._incomingStream.readable.getReader();
      this._writer = this._incomingStream.writable.getWriter();
  
      this._consumePromise = (async () => {
        try {
          while (true) {
            if (this._shouldClose) {
              console.log('StreamReader.consume should close', this._streamIndex);
              break;
            }
  
            if (!this._reader) {
              console.error(`StreamReader ${this._streamIndex}: Reader is null.`);
              break;
            }
  
            let result;
            try {
              result = await this._reader.read();
            } catch (readErr) {
              console.warn(`StreamReader ${this._streamIndex}: Reader.read() error:`, readErr);
              break;
            }
  
            const { value: data, done } = result;
  
            if (done) {
              console.log(`StreamReader.consume done ${this._streamIndex}`);
              break;
            }
  
            if (data) {
              this._handleReadBuffer(data);
            }
          }
        } catch (err) {
          console.error(`StreamReader.consume error on stream ${this._streamIndex}:`, err);
        } finally {
          if (this._timeoutId) {
            clearTimeout(this._timeoutId);
            this._timeoutId = null;
          }
          await this._cleanup();
          if (this.onClose) {
            this.onClose();
          }
        }
      })();
    }
  
    async waitForCompletion() {
      if (this._consumePromise) {
        await this._consumePromise;
      }
    }
  
    async close() {
      console.log('StreamReader.close', this._streamIndex);
      this._shouldClose = true;
  
      if (this._timeoutId) {
        clearTimeout(this._timeoutId);
        this._timeoutId = null;
      }
  
      await this._cleanup();
    }
  
    private async _cleanup() {
      if (this._isClosed) {
        return;
      }
      this._isClosed = true;
  
      if (this._reader) {
        try {
          await this._reader.cancel();
          console.log(`StreamReader ${this._streamIndex}: Reader canceled.`);
        } catch (err) {
          console.warn(`StreamReader ${this._streamIndex}: Error canceling reader:`, err);
        } finally {
          try {
            this._reader.releaseLock();
          } catch (err) {
            console.warn(`StreamReader ${this._streamIndex}: Error releasing reader lock:`, err);
          }
          this._reader = null;
        }
      }
  
      if (this._writer) {
        try {
          await this._writer.close();
          console.log(`StreamReader ${this._streamIndex}: Writer closed.`);
        } catch (err) {
          console.warn(`StreamReader ${this._streamIndex}: Error closing writer:`, err);
        } finally {
          try {
            this._writer.releaseLock();
          } catch (err) {
            console.warn(`StreamReader ${this._streamIndex}: Error releasing writer lock:`, err);
          }
          this._writer = null;
        }
      }
  
      console.log(`StreamReader ${this._streamIndex}: Locks released.`);
      this._incomingStream = null;
      this.onRead = noopFunc;
      this.onDescription = noopFunc;
      this.onEos = noopFunc;
      this.onMessage = noopFunc;
      this._rxDataBuf = null;
    }
  
    private _handleReadBuffer(data: Uint8Array) {
      this._bytesCnt += data.byteLength;
  
      if (this._isClosed) {
        console.log(`StreamReader ${this._streamIndex}: Ignoring data since stream is closed.`);
        return;
      }
  
      const dataView = getDataViewOfTypedArray(this._appendBuffer(data));
      while (true) {
        const dataSize = dataView.byteLength - this._rxDataOffset;
        if (dataSize < MESSAGE_HEADER_LEN_BYTES) {
          break;
        }
  
        const msgLength = dataView.getUint32(this._rxDataOffset, true);
        if (dataSize < msgLength) {
          break;
        }
  
        this._popCurrentMessage(msgLength);
        if (this._rxDataOffset >= dataView.byteLength) {
          break;
        }
      }
  
      this._pruneBufferHead();
    }
  
    private _appendBuffer(data: Uint8Array): Uint8Array {
      if (!this._rxDataBuf) {
        this._rxDataBuf = data;
      } else {
        // Create a new buffer that only keeps unprocessed data
        const newSize = this._rxDataBuf.byteLength - this._rxDataOffset + data.byteLength;
        const newBuf = new Uint8Array(newSize);
        newBuf.set(this._rxDataBuf.subarray(this._rxDataOffset)); 
        newBuf.set(data, this._rxDataBuf.byteLength - this._rxDataOffset);
        this._rxDataBuf = newBuf;
      }
      this._rxDataOffset = 0;
      return this._rxDataBuf;
    }
  
    private _popCurrentMessage(messageLen: number) {
      if (!this._rxDataBuf) {
        throw new Error('Buffer should be initialized.');
      }
  
      const msgBufEnd = this._rxDataOffset + messageLen;
      const msgBuf = this._rxDataBuf.subarray(this._rxDataOffset, msgBufEnd);
      this._rxDataOffset = msgBufEnd;
  
      const msgData: MessageStruct = parseMessageStruct(msgBuf);
      this._processMessage(msgData);
    }
  
    private _processMessage(msgData: MessageStruct) {
      if (msgData.flagsParsed.payloadType === MessagePayloadType.Json) {
        const streamEvent = <TStreamEvent>getMessageJsonPayload(msgData);
        //console.log(streamEvent.Type);
        switch (streamEvent.Type) {
          case 'Description':
            this._handleDescription(<TStreamDescription>streamEvent);
            break;
          case 'Eos':
            this._handleEos();
            break;
          default:
            const metaData = (streamEvent as any).onMetaData?.message;

            if (metaData) {
              if (metaData.width !== undefined && metaData.height !== undefined) {
                const videoCodecInfo: VideoCodecInfo = {
                  width: metaData.width,
                  height: metaData.height,
                  codecName: getVideoCodecName(metaData.videocodecid || 0),
                  dataRate: metaData.videodatarate || 0,
                  // profile: '', 
                  // level: ''
                };

                this.onVideoCodecInfo(videoCodecInfo);

              } 
              
              if (metaData.audiocodecid !== undefined || metaData.audiosamplerate !== undefined) {
                const audioCodecInfo: AudioCodecInfo = {
                  sampleRate: metaData.audiosamplerate || 0,
                  dataRate: metaData.audiodatarate || 0,
                  channels: metaData.stereo ? 2 : 1,
                  codecName: getAudioCodecName(metaData.audiocodecid || 0),
                  // profile: ''
                };

                this.onAudioCodecInfo(audioCodecInfo);
              }
            }
            break;
        }
      }
  
      const segmentIndex = this._segmentList.length - 1;
      const currentSegment = this._segmentList[segmentIndex];
      currentSegment.buffer.push(msgData);
      if (this.onMessage) {
        this.onMessage(msgData, this._groupId, this._tag);
      }
      this._continueRead();
    }
  
    private _handleDescription(description: TStreamDescription) {
      const segmentIndex = this._segmentList.length;
      const newSegment: StreamSegment = {
        description,
        isEnded: false,
        buffer: [],
        bufferReadOffset: 0,
      };
      this._segmentList.push(newSegment);
      this._segmentMap.set(description.GroupId, [segmentIndex, newSegment]);
      this.onDescription(segmentIndex, description);
    }
  
    private _handleEos() {
      const segmentIndex = this._segmentList.length - 1;
      this._segmentList[segmentIndex].isEnded = true;
      this.onEos(segmentIndex);
    }
  
    private _continueRead() {
      this._segmentList.forEach((segment, index) => {
        while (segment.bufferReadOffset < segment.buffer.length) {
          const message = segment.buffer[segment.bufferReadOffset++];
          if (segment.description) {
            this.onRead(message, segment.description.GroupId, segment.description.Tag, index);
          }
        }
        // Remove processed messages from the buffer to prevent memory leak
        if (segment.bufferReadOffset > 0) {
          segment.buffer.splice(0, segment.bufferReadOffset);
          segment.bufferReadOffset = 0;
        }
        if (segment.isEnded) {
          segment.buffer = [];
          segment.bufferReadOffset = 0;
        }
      });
    }
  
    private _pruneBufferHead() {
      if (this._rxDataBuf && this._rxDataOffset > 0) {
        this._rxDataBuf = this._rxDataBuf.subarray(this._rxDataOffset);
        this._rxDataOffset = 0;
      }
    }
  
    private _adaptReadTimeout() {
      const meanMsgDeltaMs = this._accuMsgDeltaMs / this._accuMsgCnt;
      this._currentMeanMsgDeltaMs = meanMsgDeltaMs;
      this.readTimeoutMs = Math.max(
        this.minReadTimeoutMs,
        Math.round(5 * this._currentMeanMsgDeltaMs)
      );
    }
  
    get groupId(): number {
      return this._groupId;
    }
  
    get tag(): string {
      return this._tag;
    }
  
    get streamIndex(): number {
      return this._streamIndex;
    }
  }
  